import BaseObjectDirective from './BaseObjectDirective';

/**
 * AsfGeoGebraObjectCtrl - Controller for handling GeoGebra objects within the application.
 * Extends BaseObjectDirective to inherit common functionalities.
 */
export class AsfGeoGebraObjectCtrl extends BaseObjectDirective {
    static APPLET_LOAD_TIMEOUT = 500;
    static GEOGEBRA_SCRIPT_URL = '/thirdparty/geogebra/deployggb.js';
    static GEOGEBRA_CODEBASE = '/thirdparty/geogebra/HTML5/5.0/web3d/';
    static GEOGEBRA_EVENTS = {
        update: 'Update event',
        add: 'Object added',
        remove: 'Object removed',
        clear: 'Construction cleared'
    };

    constructor($injector, $scope, $rootScope, $signals, $interval, $timeout, $uibModal, TaskDataService, TaskInfoService, $config) {
        super($injector, $scope);

        this.services = {
            $injector, $scope, $rootScope, $signals,
            $interval, $timeout, $uibModal,
            TaskDataService, TaskInfoService, $config
        };

        this.state = {
            isInitializing: true,
            isDestroyed: false,
            hasUserChanges: false,
            initialState: null,
            appletApi: null,
            modalInstance: null,
            latestScreenshots: null,
            editGeneration: 0
        };

        this.initializeScope();
        this.setupEventListeners();
    }

    initializeScope() {
        const { $scope, $rootScope, TaskInfoService, $config } = this.services;
        const contentID = $scope.contentObject.getUUID();

        Object.assign($scope, {
            getScope: () => $scope,
            objectOutFocus: this.objectOutFocus.bind(this),
            appletID: `ggbApplet${contentID.replace(/-/g, '')}`,
            isCMS: $rootScope.isCMS && !$rootScope.isPreview,
            isPreview: $rootScope.isPreview,
            editMode: false,
            prefsMode: false,
            hasBrowseGeogebra: true,
            isPDFMode: $config.isPDFMode(),
            filled: false,
            onMediaBrowseButtonClick: () => this.onMediaBrowseButtonClick(),
            onSelectMedia: (media) => this.onSelectMedia(media)
        });

        const taskInfo = TaskInfoService.getTaskInfo();
        $scope.writingDirection = $rootScope.isJournal ? "ltr" : taskInfo.getInputLanguage().writingDirection;
        $scope.langISO = taskInfo.getISOInputLanguageCode();
    }

    setupEventListeners() {
        const { $scope } = this.services;

        if (!$scope.isPDFMode) {
            this.initializeGeoGebra();
        }
    }

    async initializeGeoGebra() {
        if (this.state.isDestroyed) return;

        const { $scope } = this.services;

        if ($scope.isPDFMode) {
            this.initializePDFMode();
            return;
        }

        try {
            await this.loadGeoGebraScript();
            await this.createGeoGebraApplet();
        } catch (error) {
            console.error('[Geogebra] Failed to initialize:', error);
        }
    }

    setupGeoGebraListeners(api) {
        const debouncedRefresh = this._debounce(() => {
            if (this.state.isInitializing || this.state.isDestroyed) return;
            this.checkForChanges();
            if (this.state.hasUserChanges) {
                this._refreshLatestScreenshots();
            }
        }, 500);

        Object.entries(AsfGeoGebraObjectCtrl.GEOGEBRA_EVENTS).forEach(([event, logMessage]) => {
            const handler = (...args) => {
                if (!this.state.isInitializing) {
                    console.log(`[Geogebra] ${logMessage}`, ...args);
                    this.state.hasUserChanges = true;
                    this.state.editGeneration++;
                    this.state.latestScreenshots = null;
                    if (event === 'update') {
                        debouncedRefresh();
                    }
                }
            };

            const methodName = `register${event.charAt(0).toUpperCase() + event.slice(1)}Listener`;
            api[methodName](handler);
        });
    }

    async _refreshLatestScreenshots() {
        if (this.state.isDestroyed || !this.state.appletApi) return;
        const startGeneration = this.state.editGeneration;
        try {
            const screenshots = await this.getScreenShots();
            if (this.state.isDestroyed) return;
            if (this.state.editGeneration !== startGeneration) return;
            if (!screenshots.screenshotBase64 && !screenshots.svg) return;
            this.state.latestScreenshots = screenshots;
        } catch (e) {
            console.error('[Geogebra] Failed to refresh latest screenshots:', e);
            if (window.Sentry) {
                window.Sentry.captureException(e, { tags: { context: 'geogebra_screenshot_refresh' } });
            }
        }
    }

    _debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    async onAppletLoad(api) {
        if (this.state.isDestroyed) return;

        console.log("[Geogebra] Applet loaded");
        this.state.appletApi = api;
        this.setupGeoGebraListeners(api);

        await this.initializeAppletContent();
    }

    async initializeAppletContent() {
        if (this.state.isDestroyed) return;

        const { $scope, $timeout } = this.services;

        try {
            if (this.isCMSAndNotPDFMode()) {
                await $timeout(() => this.setupCMSInterface());
            }

            await this.loadInitialContent();

            if (this.state.isDestroyed) return;

            $scope.filled = true;
            this.state.isInitializing = false;
            $scope.$apply();
        } catch (error) {
            console.error('[Geogebra] Failed to initialize content:', error);
        }
    }

    async loadInitialContent() {
        if (this.state.isDestroyed) return;

        const { $scope } = this.services;
        const attachment = $scope.contentObject.getAttachement();

        try {
            if (attachment?.ggbResponse) {
                await this.state.appletApi.setBase64(attachment.ggbResponse);
            }

            if (this.state.isDestroyed || !this.state.appletApi) return;

            this.state.initialState = {
                base64: this.state.appletApi.getBase64(),
                xml: this.state.appletApi.getXML()
            };
        } catch (error) {
            console.error('[Geogebra] Failed to load initial content:', error);
            this.state.initialState = { base64: '', xml: '' };
        }
    }

    checkForChanges() {
        if (this.state.isDestroyed || !this.state.initialState || !this.state.appletApi || this.state.isInitializing) return;

        const currentBase64 = this.state.appletApi.getBase64();
        if (currentBase64 !== this.state.initialState.base64) {
            this.state.hasUserChanges = true;
            console.log("[Geogebra] Content changed by user (detected by base64)");
            return;
        }

        const currentXML = this.state.appletApi.getXML();
        if (!this.compareGeoXML(this.state.initialState.xml, currentXML)) {
            this.state.hasUserChanges = true;
            console.log("[Geogebra] Content changed by user (detected by XML)");
        }
    }

    async objectOutFocus() {
        if (this.state.isDestroyed || !this.state.hasUserChanges) return;

        try {
            if (this.isCMSAndNotPDFMode()) {
                await this.saveCMSContent();
            } else {
                await this.saveResponseData('geoGebraUpdate');
            }
        } catch (error) {
            console.error('[Geogebra] Error saving content:', error);
        }
    }

    async saveCMSContent() {
        const screenshots = await this.getScreenShots();
        if (this.state.isDestroyed || !this.state.appletApi) return;
        this.services.$scope.contentObject.setContent({
            base64Data: this.state.appletApi.getBase64(),
            xmlData: this.state.appletApi.getXML(),
            screenshotBase64: screenshots.screenshotBase64,
            svg: screenshots.svg
        });
    }

    async cleanup() {
        // Cache GeoGebra data BEFORE destroying the applet. Capture base64/XML and
        // screenshots in separate try blocks so a failure in one doesn't drop the
        // other — the student's actual answer (base64/XML) is more important than
        // the preview screenshot. Screenshots come from the rolling cache populated
        // on debounced updates; the cache is invalidated on every edit, so the
        // `??` fallback exercises a fresh sync capture whenever there are unflushed
        // edits at destroy time.
        if (this.state.appletApi && !this.state.isInitializing && this.state.hasUserChanges) {
            const cached = {
                ggbResponse: null,
                ggbResponseXML: null,
                ggbScreenshotBase64: null,
                ggbSvg: null
            };

            try {
                cached.ggbResponse = this.state.appletApi.getBase64();
                cached.ggbResponseXML = this.state.appletApi.getXML();
            } catch (e) {
                console.error('[GeoGebra] Failed to capture response payload before cleanup:', e);
                if (window.Sentry) {
                    window.Sentry.captureException(e, { tags: { context: 'geogebra_cleanup_response' } });
                }
            }

            try {
                const screenshots = this.state.latestScreenshots ?? await this.getScreenShots();
                cached.ggbScreenshotBase64 = screenshots.screenshotBase64;
                cached.ggbSvg = screenshots.svg;
            } catch (e) {
                console.error('[GeoGebra] Failed to capture screenshots before cleanup:', e);
                if (window.Sentry) {
                    window.Sentry.captureException(e, { tags: { context: 'geogebra_cleanup_screenshot' } });
                }
            }

            this.state.cachedResponseData = cached;

            if (!cached.ggbScreenshotBase64 && !cached.ggbSvg && window.Sentry) {
                window.Sentry.captureMessage('GeoGebra cleanup cached empty screenshots', {
                    level: 'warning',
                    tags: { context: 'geogebra_cleanup_empty_screenshots' }
                });
            }
        }

        this.state.isDestroyed = true;

        const { $scope } = this.services;

        if (this.state.appletApi) {
            Object.keys(AsfGeoGebraObjectCtrl.GEOGEBRA_EVENTS).forEach(event => {
                const methodName = `unregister${event.charAt(0).toUpperCase() + event.slice(1)}Listener`;
                if (this.state.appletApi[methodName]) {
                    this.state.appletApi[methodName]();
                }
            });
            this.state.appletApi = null;
        }

        if (window[$scope.appletID]) {
            delete window[$scope.appletID];
        }

        if (this.observer) {
            this.observer.disconnect();
        }

        if (this._xmlCache) {
            this._xmlCache.clear();
            this._xmlCache = null;
        }

        await super.cleanup();
    }

    async loadGeoGebraScript() {
        const existingScript = document.querySelector(
            `script[src="${AsfGeoGebraObjectCtrl.GEOGEBRA_SCRIPT_URL}"]`
        );

        if (existingScript) {
            await this._waitForGGBApplet();
            return;
        }

        try {
            await this._loadScript(AsfGeoGebraObjectCtrl.GEOGEBRA_SCRIPT_URL);
            await this._waitForGGBApplet();
        } catch (error) {
            throw new Error(`Failed to load GeoGebra script: ${error.message}`);
        }
    }

    _loadScript(url) {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = url;
            script.onload = () => resolve();
            script.onerror = (e) => reject(e);
            document.head.appendChild(script);
        });
    }

    _waitForGGBApplet(timeout = 5000) {
        return new Promise((resolve, reject) => {
            const startTime = Date.now();

            const checkGGBApplet = () => {
                if (window.GGBApplet) {
                    resolve();
                    return;
                }

                if (Date.now() - startTime > timeout) {
                    reject(new Error('Timeout waiting for GGBApplet'));
                    return;
                }

                setTimeout(checkGGBApplet, 100);
            };

            checkGGBApplet();
        });
    }

    async createGeoGebraApplet() {
        if (this.state.isDestroyed) return;

        const { $scope } = this.services;
        const containerWidth = $scope.$parent.$parent.size;
        const width = containerWidth === 50 ? 415 : 919;
        const height = this.getPreferenceOrDefault('containerHeight', 600);

        const params = {
            id: $scope.appletID,
            appName: "classic",
            width,
            height,
            allowStyleBar: this.getPreferenceOrDefault('showStylebar', false),
            showToolBar: !$scope.isPDFMode && this.getPreferenceOrDefault('showToolBar', false),
            showAlgebraInput: this.isCMSAndNotPDFMode() ? true : this.getPreferenceOrDefault('showAlgebraInput', false),
            showMenuBar: this.isCMSAndNotPDFMode(),
            useBrowserForJS: true,
            showResetIcon: !$scope.isPDFMode,
            enableRightClick: this.isCMSAndNotPDFMode() ? true : this.getPreferenceOrDefault('enableRightClick', false),
            ggbBase64: this.getInitialBase64(),
            enableShiftDragZoom: this.isCMSAndNotPDFMode() ? true : this.getPreferenceOrDefault('enableZoom', true),
            appletOnLoad: (api) => this.onAppletLoad(api),
            errorDialogsActive: false,
            preventFocus: false,
            scaleContainerClass: 'geogebra-container',
            allowUpscale: false,
            playButton: false,
            showFullscreenButton: false,
            scale: 1.0,
            autoHeight: true,
            showZoomButtons: true,
            buttonShadows: false,
            buttonBorders: true
        };

        try {
            const applet = new GGBApplet(params, true);
            applet.setHTML5Codebase(AsfGeoGebraObjectCtrl.GEOGEBRA_CODEBASE);
            applet.setPreviewImage(
                "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAIAAACQd1PeAAAACXBIWXMAAAsTAAALEwEAmpwYAAAADElEQVR4nGP4//8/AAX+Av4N70a4AAAAAElFTkSuQmCC",
                "https://www.geogebra.org/images/GeoGebra_loading.png"
            );

            const container = document.getElementById(`${$scope.contentObject.getUUID()}_geogebra`);
            if (!container) {
                throw new Error('Container element not found');
            }

            Object.assign(container.style, {
                width: `${width}px`,
                height: `${height}px`,
                maxWidth: '100%',
                overflow: 'hidden'
            });

            container.innerHTML = '';
            applet.inject(container.id);
        } catch (error) {
            console.error('[Geogebra] Failed to create applet:', error);
            throw error;
        }
    }

    getPreferenceOrDefault(key, defaultValue) {
        return this.services.$scope.contentObject?.getPreferences()?.[key] ?? defaultValue;
    }

    isCMSAndNotPDFMode() {
        return this.services.$scope?.isCMS && !this.services.$scope?.isPDFMode;
    }

    getInitialBase64() {
        const content = this.services.$scope.contentObject?.getContent();

        if (content?.base64Data) {
            return content.base64Data;
        }

        const attachment = this.services.$scope.contentObject?.getAttachement();
        if (typeof attachment === 'string' && attachment) {
            try {
                const { base64Data } = JSON.parse(attachment);
                if (base64Data) {
                    this.services.$scope.contentObject?.setContent(base64Data);
                    this.services.$scope.contentObject?.setAttachement(null);
                    return base64Data;
                }
            } catch (error) {
                console.error('[Geogebra] Failed to parse attachment:', error);
            }
        }

        return '';
    }

    async getScreenShots() {
        if (this.state.isDestroyed || !this.state?.appletApi) {
            console.warn("[Geogebra] Cannot get screenshots, applet API not ready or destroyed");
            return { svg: null, screenshotBase64: null };
        }

        if (!this.state.appletApi?.isReady?.()) {
            await new Promise(resolve => setTimeout(resolve, 1000));
        }

        if (this.state.isDestroyed || !this.state.appletApi) {
            console.warn("[Geogebra] Component destroyed during screenshot wait");
            return { svg: null, screenshotBase64: null };
        }

        return new Promise((resolve) => {
            try {
                const api = this.state.appletApi;
                if (!api) {
                    resolve({ svg: null, screenshotBase64: null });
                    return;
                }

                api.getScreenshotBase64((screenshotBase64) => {
                    if (this.state.isDestroyed || !this.state.appletApi) {
                        resolve({ svg: null, screenshotBase64: null });
                        return;
                    }
                    this.state.appletApi.exportSVG((svg) => {
                        resolve({ svg, screenshotBase64 });
                    });
                });
            } catch (error) {
                console.error("[Geogebra] Screenshot error:", error);
                if (window.Sentry) {
                    window.Sentry.captureException(error, { tags: { context: 'geogebra_screenshot_capture' } });
                }
                resolve({ svg: null, screenshotBase64: null });
            }
        });
    }

    async responseCollect(attachement, triggeredBy) {
        // Skip-if-null on every assignment: a failed capture (e.g. getScreenShots
        // racing with destroy and returning {null, null}) must not clobber a good
        // value already in `attachement` from a previous save. The base64/XML
        // sync calls almost always succeed when the entry guards passed, but the
        // truthy check also defends against degenerate empty-string returns.

        // Fallback: use cached data if applet is already destroyed
        if ((this.state.isDestroyed || !this.state.appletApi) && this.state.cachedResponseData) {
            const c = this.state.cachedResponseData;
            let dirty = false;
            if (c.ggbResponse) { attachement.ggbResponse = c.ggbResponse; dirty = true; }
            if (c.ggbResponseXML) { attachement.ggbResponseXML = c.ggbResponseXML; dirty = true; }
            if (c.ggbScreenshotBase64) { attachement.ggbScreenshotBase64 = c.ggbScreenshotBase64; dirty = true; }
            if (c.ggbSvg) { attachement.ggbSvg = c.ggbSvg; dirty = true; }
            this.state.cachedResponseData = null;
            return dirty;
        }

        if (this.state.isDestroyed || this.services.$scope.isPDFMode || !this.state.appletApi || this.state.isInitializing) {
            return false;
        }

        if (!this.state.hasUserChanges && triggeredBy !== 'force') {
            return false;
        }

        const base64 = this.state.appletApi.getBase64();
        const xml = this.state.appletApi.getXML();
        if (base64) attachement.ggbResponse = base64;
        if (xml) attachement.ggbResponseXML = xml;

        const screenshots = await this.getScreenShots();
        if (screenshots.screenshotBase64) attachement.ggbScreenshotBase64 = screenshots.screenshotBase64;
        if (screenshots.svg) attachement.ggbSvg = screenshots.svg;

        return true;
    }

    compareGeoXML(xml1, xml2) {
        try {
            if (xml1 === xml2) return true;
            if (!xml1 || !xml2) return false;

            if (!this._xmlCache) {
                this._xmlCache = new Map();
            }

            const getXmlDoc = (xml) => {
                if (this._xmlCache.has(xml)) {
                    return this._xmlCache.get(xml);
                }
                const parser = new DOMParser();
                const doc = parser.parseFromString(xml, "application/xml");
                this._xmlCache.set(xml, doc);
                return doc;
            };

            const xmlDoc1 = getXmlDoc(xml1);
            const xmlDoc2 = getXmlDoc(xml2);

            if (xmlDoc1.getElementsByTagName("parsererror").length > 0 ||
                xmlDoc2.getElementsByTagName("parsererror").length > 0) {
                return false;
            }

            return this.compareNodes(xmlDoc1.documentElement, xmlDoc2.documentElement);
        } catch (error) {
            console.error("[Geogebra] XML comparison error:", error);
            return false;
        }
    }

    compareNodes(node1, node2) {
        if (!node1 || !node2) return false;
        if (node1.nodeType !== node2.nodeType) return false;

        if (node1.nodeType === Node.ELEMENT_NODE) {
            if (['size', 'coordsystem', 'toolbar', 'window', 'selection']
                .includes(node1.tagName.toLowerCase())) {
                return true;
            }

            if (node1.tagName !== node2.tagName) return false;

            const attributes1 = Array.from(node1.attributes || []);
            const attributes2 = node2.attributes;

            if (attributes1.length !== attributes2.length) return false;

            for (const attr1 of attributes1) {
                if (node1.tagName.toLowerCase() === 'pane' && attr1.name === 'divider') {
                    continue;
                }
                const attr2 = attributes2.getNamedItem(attr1.name);
                if (!attr2 || attr1.value !== attr2.value) return false;
            }
        }

        if (node1.nodeType === Node.TEXT_NODE) {
            return (node1.textContent || '').trim() === (node2.textContent || '').trim();
        }

        const children1 = Array.from(node1.childNodes || [])
            .filter(node => node.nodeType !== Node.COMMENT_NODE);
        const children2 = Array.from(node2.childNodes || [])
            .filter(node => node.nodeType !== Node.COMMENT_NODE);

        return children1.length === children2.length &&
            children1.every((child, i) => this.compareNodes(child, children2[i]));
    }

    browseModalInstanceController = ($scope, $uibModalInstance, contentObjectUUID) => {
        Object.assign($scope, {
            showonly: 'geogebra',
            contentobjectuuid: contentObjectUUID,
            onModalCloseButtonClick: () => $uibModalInstance.dismiss('cancel')
        });
    }


    initPreferences() {
        const { $scope, $timeout } = this.services;
        const contentID = $scope.contentObject.getUUID();
        const appletID = $scope.appletID;

        const predefinedSize = $scope.$parent.$parent.size === 100 ? '919' : '414';

        const defaultPreferences = {
            width: predefinedSize,
            placeholder: null,
            label: null,
            includedInPDF: true,
            showToolBar: false,
            showStylebar: false,
            enableRightClick: false,
            showAlgebraInput: false,
            containerHeight: 600,
            enableZoom: true
        };

        $scope.preferencesValues = Object.entries(defaultPreferences).reduce((acc, [key, defaultValue]) => {
            const value = this.getPreferenceOrDefault(key, defaultValue);

            if (key === 'containerHeight') {
                acc[key] = value >= 600 && value <= 1200 ? value : 600;
            } else {
                acc[key] = value;
            }

            return acc;
        }, {});

        $scope.$watch('preferencesValues', (newVal, oldVal) => {
            if (newVal === oldVal) return;

            const newHeight = newVal.containerHeight;
            if (newHeight >= 600 && newHeight <= 1200) {
                window[appletID]?.setHeight(newHeight);
                const container = document.getElementById(`${contentID}_geogebra`);
                if (container) {
                    container.style.height = `${newHeight}px`;
                }
            }

            $scope.contentObject?.setPreferences(newVal);
        }, true);

        $scope.preferences = [
            {
                title: [
                    { type: 'title', name: 'Settings', valueKey: 'CandidateViewSettings' }
                ],
                includedInPDF: [
                    { type: 'checkbox', name: 'Included in Response PDF', valueKey: 'includedInPDF' }
                ],
                containerHeight: [
                    { type: 'input', name: 'Container height', valueKey: 'containerHeight' }
                ]
            },
            {
                title: [
                    { type: 'title', name: 'Candidate View Settings', valueKey: 'CandidateViewSettings' }
                ],
                showToolBar: [
                    { type: 'checkbox', name: 'Show toolbar', valueKey: 'showToolBar' }
                ],
                showStylebar: [
                    { type: 'checkbox', name: 'Show stylebar', valueKey: 'showStylebar' }
                ],
                enableRightClick: [
                    { type: 'checkbox', name: 'Enable right click features', valueKey: 'enableRightClick' }
                ],
                showAlgebraInput: [
                    { type: 'checkbox', name: 'Enable algebra input field', valueKey: 'showAlgebraInput' }
                ],
                enableZoom: [
                    { type: 'checkbox', name: 'Enable zooming and dragging', valueKey: 'enableZoom' }
                ]
            }
        ];
    }
}

export default function registerAsfGeoGebraObjectDirective(module) {
    module.directive('asfGeoGebraObject', function ($compile, $bridges, $config, TaskDataService, $rootScope, $interval, $http, $signals) {
        return {
            restrict: 'EA',
            replace: true,
            scope: {
                contentObject: "="
            },
            templateUrl: "/views/ib/contentobjects/geogebra.html",
            controller: AsfGeoGebraObjectCtrl
        };
    });

    module.run(['ContentObjectRegistryService', function (ContentObjectRegistryService) {
        const obj = [{
            uuid: "",
            content: "",
            objectType: "geo-gebra",
            type: "contentObject",
            entity_content: "",
            preferences: {
                width: "",
                placeholder: "",
                label: "",
                includedInPDF: true
            }
        }];

        const objProperties = {
            type: "geo-gebra",
            isResponse: true,
            label: 'CMS.LAYOUT-MANAGER.NEW-OBJECT.GEOGEBRA.LABEL',
            cssClass: "new-short-textbox-object",
            icon: "cms/icon-response-textbox-short.png",
            description: 'CMS.LAYOUT-MANAGER.NEW-OBJECT.GEOGEBRA.DESCRIPTION',
            group: { id: 3 }
        };

        ContentObjectRegistryService.register(obj, objProperties);
    }]);
}
